import React, { useContext } from 'react';
import { Radio, Row, Col, Space, Button, Divider } from 'antd';
import { ConfigContext } from '../../context/Config';
import moment from 'moment';
import Text from '../Text';
import { useWindowSize } from '../../hooks/useWindowSize';
import IconArrowRight from '../Icons/IconArrowRight';
import IconArrowLeft from '../Icons/IconArrowLeft';
import './CalendarToolbar.less';

export type CalendarView = 'month' | 'day' | 'week' | 'agenda';
export type OnFilterChangeType = (params: {
  calendarView?: CalendarView;
  date?: Date;
  label?: string;
  startDate?: string;
  endDate?: string;
}) => void;

export type CalendarToolbarProps = {
  date: Date;
  calendarView?: CalendarView;
  changeCalendarView: (calendarView: CalendarView) => void;
  changeDate: (date: Date) => void;
  onFilterChange: OnFilterChangeType;
};

const CalendarToolbar: React.FC<CalendarToolbarProps> = (
  { date, changeDate, onFilterChange, calendarView, changeCalendarView },
  props,
) => {
  const { translate } = useContext(ConfigContext);
  const size = useWindowSize();
  const isMobile = size.width < 426;

  const { label } = props;

  const colCustomStyle = {
    xs: { span: 24 },
    sm: { span: 12, push: 0 },
    md: { span: 24, push: 10 },
    lg: { span: 24, push: 11 },
    xl: { span: 7, push: 0 },
    xxl: { span: 6, push: 0 },
    style: {
      marginBottom: 15,
    },
  };

  const monthColStyle = {
    ...colCustomStyle,
    xs: { span: 24, push: 0 },
    md: { span: 24, push: 0 },
    lg: { span: 24, push: 0 },
  };

  const btnGroupColStyle = {
    ...colCustomStyle,
    xs: { span: 24 },
    md: { span: 24, pull: 5 },
    lg: { span: 24, pull: 7 },
    xl: { span: 8, pull: 0 },
  };

  const renderRightSection = () => {
    return (
      <Space
        size={16}
        split={<Divider type="vertical" style={{ height: '1.5em' }} />}
      >
        <Button
          onClick={() => {
            const view = calendarView === 'agenda' ? 'month' : calendarView;
            const today = moment().startOf(view);
            changeDate(new Date(today as any));
            onFilterChange({ calendarView: view });
          }}
        >
          {translate('Today')}
        </Button>
        <Radio.Group
          className="button-group"
          buttonStyle="solid"
          defaultValue={calendarView}
          onChange={(val) => changeCalendarView(val.target.value)}
        >
          {!isMobile && (
            <>
              <Radio.Button value="month">{translate('ui.month')}</Radio.Button>
              <Radio.Button value="week">{translate('ui.week')}</Radio.Button>
              <Radio.Button value="day">{translate('ui.day')}</Radio.Button>
            </>
          )}
          <Radio.Button value="agenda">{translate('ui.agenda')}</Radio.Button>
        </Radio.Group>
      </Space>
    );
  };

  return (
    <Row gutter={24} justify={'space-between'}>
      <Col
        {...colCustomStyle}
        style={{
          marginBottom: 15,
          textAlign: isMobile ? 'center' : 'start',
        }}
      >
        <Text level={1} bold>
          {translate('ui.calendar')}
        </Text>
      </Col>

      <Col {...monthColStyle}>
        <Row
          gutter={24}
          justify="center"
          style={{ alignItems: 'center', marginBottom: 15 }}
        >
          {/* Arrow Left */}
          <Col
            onClick={() => {
              const view = calendarView === 'agenda' ? 'month' : calendarView;
              const momentStartDate = moment(date).add(-1, view).startOf(view);
              changeDate(new Date(momentStartDate.toString()));
              onFilterChange({
                startDate: momentStartDate.toISOString(),
                endDate: moment(date).add(-1, view).endOf(view).toISOString(),
                date: moment(date).add(-1, view).toDate(),
                calendarView: view,
              });
            }}
          >
            <span className="t-arrowIconStyle">
              <IconArrowLeft size="md" />
            </span>
          </Col>

          {/* Month Label */}
          <Col>
            <Text level={isMobile ? 2 : 1} rows={1} semiBold>
              {label}
            </Text>
          </Col>

          {/* Arrow Right */}
          <Col
            onClick={() => {
              const view = calendarView === 'agenda' ? 'month' : calendarView;
              const momentStartDate = moment(date).add(1, view).startOf(view);
              changeDate(new Date(momentStartDate.toString()));
              onFilterChange({
                startDate: momentStartDate.toISOString(),
                endDate: moment(date).add(1, view).endOf(view).toISOString(),
                date: moment(date).add(1, view).toDate(),
                calendarView: view,
              });
            }}
          >
            <span className="t-arrowIconStyle">
              <IconArrowRight size="md" />
            </span>
          </Col>
        </Row>
      </Col>

      <Col
        {...btnGroupColStyle}
        style={{
          textAlign: isMobile ? 'center' : 'end',
          marginBottom: 15,
        }}
      >
        {renderRightSection()}
      </Col>
    </Row>
  );
};

export default CalendarToolbar;
